import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'
import useSWR from 'swr'

import {
  PreferenceButton,
  PreferenceCategory,
  PreferenceInfo,
  PreferenceInput,
  PreferenceScreen,
  PreferenceSelect,
  PreferenceSubmit,
  PreferenceUpload,
} from '../../components/Preference'
import { fetcher } from '../../lib/swr'
import type { FullList, ListAccessLevel } from '../../pg/types'

const ACCESS_OPTIONS: Array<{
  value: ListAccessLevel
  label: string
  summary: string
}> = [
  {
    value: 'tenant',
    label: 'Alleen huishouden',
    summary: 'Alleen jouw huishouden ziet deze lijst',
  },
  {
    value: 'follower',
    label: 'Volgers',
    summary: 'Huishouden + mensen die jullie volgen',
  },
  {
    value: 'everyone',
    label: 'Iedereen',
    summary: 'Zichtbaar in het publieke kookboek',
  },
]

export default function ListDetailSettings() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const lists = useSWR<FullList[]>('/api/lists', fetcher)
  const list = lists.data?.find((l) => String(l.id) === id) ?? null

  const [draftName, setDraftName] = useState('')
  const [draftAccess, setDraftAccess] = useState<ListAccessLevel>('everyone')
  const [busy, setBusy] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [ready, setReady] = useState(false)
  /** Bust img cache after upload/clear. */
  const [imageRev, setImageRev] = useState(0)

  useEffect(() => {
    if (lists.error) {
      toast.error(lists.error.message || 'Kon lijst niet laden')
    }
  }, [lists.error])

  useEffect(() => {
    if (!list) return
    setDraftName(list.display)
    setDraftAccess(list.accessLevel || 'everyone')
    setReady(true)
  }, [list])

  const dirty =
    list &&
    ready &&
    (list.display !== draftName.trim() || list.accessLevel !== draftAccess)

  async function saveList() {
    if (!list) return
    const display = draftName.trim()
    if (!display) {
      toast.error('Naam is verplicht')
      return
    }
    setBusy(true)
    try {
      const res = await fetch(`/api/lists/${list.id}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          display,
          accessLevel: draftAccess,
        }),
      })
      const data = await res.json()
      if (!res.ok) {
        toast.error(data.message || 'Bewaren mislukt')
        return
      }
      toast.success('Bewaard')
      await lists.mutate()
    } finally {
      setBusy(false)
    }
  }

  async function uploadImage(file: File) {
    if (!list) return
    if (!file.type.startsWith('image/')) {
      toast.error('Kies een afbeeldingsbestand')
      return
    }
    setUploading(true)
    try {
      const res = await fetch(`/api/lists/${list.id}/image`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': file.type || 'application/octet-stream',
        },
        body: file,
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        toast.error(data.message || 'Upload mislukt')
        return
      }
      toast.success('Afbeelding gezet')
      setImageRev((n) => n + 1)
      await lists.mutate()
    } finally {
      setUploading(false)
    }
  }

  async function removeImage() {
    if (!list?.hasImage) return
    setUploading(true)
    try {
      const res = await fetch(`/api/lists/${list.id}/image`, {
        method: 'DELETE',
        credentials: 'include',
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        toast.error(data.message || 'Verwijderen mislukt')
        return
      }
      toast.success('Afbeelding verwijderd')
      setImageRev((n) => n + 1)
      await lists.mutate()
    } finally {
      setUploading(false)
    }
  }

  async function removeList() {
    if (!list) return
    if (!confirm(`Lijst “${list.display}” verwijderen?`)) return
    setBusy(true)
    try {
      const res = await fetch(`/api/lists/${list.id}`, {
        method: 'DELETE',
        credentials: 'include',
      })
      const data = await res.json()
      if (!res.ok) {
        toast.error(data.message || 'Verwijderen mislukt')
        return
      }
      toast.success('Lijst verwijderd')
      await lists.mutate()
      navigate('/settings/lists', { replace: true })
    } finally {
      setBusy(false)
    }
  }

  if (lists.isLoading && !list) {
    return (
      <PreferenceScreen title="Lijst" backHref="/settings/lists">
        <PreferenceInfo title="Laden…" />
      </PreferenceScreen>
    )
  }

  if (!list) {
    return (
      <PreferenceScreen title="Lijst" backHref="/settings/lists">
        <PreferenceCategory filled>
          <PreferenceInfo
            title="Niet gevonden"
            summary="Deze lijst bestaat niet (meer)."
          />
        </PreferenceCategory>
      </PreferenceScreen>
    )
  }

  return (
    <PreferenceScreen title={list.display} backHref="/settings/lists">
      <PreferenceCategory title="Bewerken" filled>
        <PreferenceInput
          title="Naam"
          value={draftName}
          onChange={setDraftName}
          placeholder="Summer"
          autoFocus
        />
        <PreferenceSelect
          title="Toegang"
          value={draftAccess}
          options={ACCESS_OPTIONS}
          onValueChange={(v) => setDraftAccess(v as ListAccessLevel)}
        />
        <PreferenceSubmit
          title="Bewaren"
          onPress={saveList}
          disabled={busy || !dirty}
        />
      </PreferenceCategory>

      <PreferenceCategory title="Omslag" filled>
        {list.hasImage ? (
          <div className="px-5 py-3">
            <img
              src={`/api/lists/${list.id}/image?v=${imageRev}`}
              alt=""
              className="w-full max-h-48 object-cover rounded-xl ring-1 ring-white/10"
            />
          </div>
        ) : (
          <PreferenceInfo
            title="Geen afbeelding"
            summary="Zonder omslag toont het kookboek de naam van het eerste recept."
          />
        )}
        <PreferenceUpload
          title={list.hasImage ? 'Andere afbeelding' : 'Kies afbeelding'}
          summary="JPEG, PNG of WebP · max 50 MB"
          accept="image/jpeg,image/png,image/webp,image/gif"
          onFileSelect={uploadImage}
          isUploading={uploading}
        />
        {list.hasImage ? (
          <PreferenceButton
            title="Verwijder afbeelding"
            onPress={removeImage}
            disabled={uploading || busy}
            isDestructive
          />
        ) : null}
      </PreferenceCategory>

      <PreferenceCategory
        title={`${list.recipes.length} recept${list.recipes.length === 1 ? '' : 'en'}`}
        filled
      >
        {list.recipes.length === 0 ? (
          <PreferenceInfo
            title="Leeg"
            summary="Voeg recepten toe via het kookboek."
          />
        ) : (
          list.recipes
            .slice(0, 8)
            .map((r) => (
              <PreferenceInfo key={r.id} title={r.display || 'Recept'} />
            ))
        )}
        {list.recipes.length > 8 ? (
          <PreferenceInfo
            title={`+${list.recipes.length - 8} meer`}
            summary="Bekijk in het kookboek"
          />
        ) : null}
      </PreferenceCategory>

      <PreferenceCategory filled>
        <PreferenceButton
          title="Verwijder lijst"
          onPress={removeList}
          disabled={busy}
          isDestructive
        />
      </PreferenceCategory>
    </PreferenceScreen>
  )
}
